import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirm();
 *   ...
 *   const yes = await confirm({ title: "Delete?", description: "..." });
 *   if (!yes) return;
 *   ...
 *   <ConfirmDialog />
 */
export function useConfirm() {
  const [state, setState] = useState({ open: false, title: "", description: "", confirmLabel: "Delete", variant: "destructive" });
  const [resolver, setResolver] = useState({ resolve: null });

  const confirm = (opts = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: opts.title || "Are you sure?",
        description: opts.description || "This action cannot be undone.",
        confirmLabel: opts.confirmLabel || "Delete",
        variant: opts.variant || "destructive",
      });
      setResolver({ resolve });
    });
  };

  const handle = (ok) => {
    setState((s) => ({ ...s, open: false }));
    resolver.resolve?.(ok);
  };

  const ConfirmDialog = () => (
    <AlertDialog open={state.open} onOpenChange={(v) => !v && handle(false)}>
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display">{state.title}</AlertDialogTitle>
          <AlertDialogDescription>{state.description}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="confirm-cancel" onClick={() => handle(false)}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="confirm-ok"
            onClick={() => handle(true)}
            className={state.variant === "destructive" ? "bg-red-600 hover:bg-red-700 text-white" : "bg-blue-900 hover:bg-blue-800 text-white"}
          >
            {state.confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, ConfirmDialog };
}
